/**
 * Página principal: el tablero (o la lista) con filtros, resumen y panel de detalle.
 *
 * Estado que vive aquí:
 *  - Los filtros (todos en el panel de `BarraFiltros`, incluidos Estado y «Solo riego») y la vista
 *    elegida (tablero o lista; esta se recuerda en el navegador). Arriba quedan solo «Exportar CSV»
 *    y el selector Tablero | Lista.
 *  - La tarea abierta, guardada en la dirección (`?tarea=12` o `?tarea=nueva`), así se puede
 *    compartir un enlace directo a una tarea y el botón «atrás» del navegador cierra el panel.
 */
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Estado } from '@cdtec-backlog/contrato';
import { EstadoError, EstadoVacio } from '../componentes/EstadoSeccion';
import { Icono } from '../componentes/Icono';
import { useMostrarTrasRetardo } from '../componentes/useMostrarTrasRetardo';
import { guardarPreferencia, leerPreferencia } from '../lib/almacenamiento';
import { useYo } from '../features/auth/sesion';
import { useProyectos } from '../features/proyectos/hooks';
import {
  FILTROS_INICIALES,
  FILTROS_TODAS,
  urlExportarCsv,
  type FiltrosUI,
} from '../features/tareas/api';
import { AvisoCerradas } from '../features/tareas/AvisoCerradas';
import { BarraFiltros } from '../features/tareas/BarraFiltros';
import { useConfirmarCierre } from '../features/tareas/ConfirmarCierre';
import {
  useMoverTarea,
  usePersonas,
  useTareas,
  useTareasMoviendose,
} from '../features/tareas/hooks';
import { IndicadorRefresco } from '../features/tareas/IndicadorRefresco';
import { ListaTareas, ListaTareasCargando } from '../features/tareas/ListaTareas';
import { PanelTarea } from '../features/tareas/PanelTarea';
import { ResumenCargando, ResumenTablero } from '../features/tareas/ResumenTablero';
import { Tablero, TableroCargando } from '../features/tareas/Tablero';
import './PaginaTablero.css';

type Vista = 'tablero' | 'lista';

export function PaginaTablero() {
  const yo = useYo();
  const [parametros, setParametros] = useSearchParams();
  const [filtros, setFiltros] = useState<FiltrosUI>(FILTROS_INICIALES);
  const [vista, setVista] = useState<Vista>(() =>
    leerPreferencia('vista') === 'lista' ? 'lista' : 'tablero',
  );

  const tareas = useTareas(filtros, { principal: true });
  const todas = useTareas(FILTROS_TODAS);
  const personas = usePersonas();
  const proyectos = useProyectos();
  const mover = useMoverTarea();
  const moviendo = useTareasMoviendose();
  // Cerrar una madre con subtareas abiertas o una tarea que otras esperan, o avanzar una tarea bloqueada, pide
  // confirmación antes de mover (ADR-0018).
  const { confirmarCierre, dialogo: confirmacionCierre } = useConfirmarCierre();
  // Esqueletos solo en la PRIMERA carga (sin datos) y si tarda: el polling y los cambios de filtro
  // (que conservan el resultado anterior) nunca vuelven a mostrarlos.
  const mostrarEsqueleto = useMostrarTrasRetardo(tareas.isPending || todas.isPending);

  const abierta = parametros.get('tarea');
  const idAbierta =
    abierta === 'nueva' ? 'nueva' : abierta && /^\d+$/.test(abierta) ? Number(abierta) : null;
  // `?tarea=nueva&padre=12`: «Nueva subtarea» desde la ficha de CDT-12.
  const padre = parametros.get('padre');
  const padreNueva = idAbierta === 'nueva' && padre && /^\d+$/.test(padre) ? Number(padre) : null;

  // «Nueva tarea» vive en el encabezado y abre el panel con `?tarea=nueva`.
  const abrir = useCallback((id: number) => setParametros({ tarea: String(id) }), [setParametros]);
  const nueva = useCallback(() => setParametros({ tarea: 'nueva' }), [setParametros]);
  const cerrar = useCallback(() => setParametros({}), [setParametros]);
  const nuevaSubtarea = useCallback(
    (madreId: number) => setParametros({ tarea: 'nueva', padre: String(madreId) }),
    [setParametros],
  );

  // Tras confirmar en el diálogo, el foco va a la tarjeta movida (si sigue a la vista; ver `Tablero`).
  const [enfocar, setEnfocar] = useState<{ id: number; estado: Estado } | null>(null);
  const olvidarEnfocar = useCallback(() => setEnfocar(null), []);
  const moverTarjeta = (id: number, estado: Estado) => {
    const tarea = tareas.data?.items.find((t) => t.id === id);
    const seguir = () => mover.mutate({ id, estado }, { onError: () => setEnfocar(null) });
    if (!tarea) {
      seguir();
      return;
    }
    // Solo si hubo diálogo: sin él, el foco se queda donde estaba (el botón ← → o nada al arrastrar).
    confirmarCierre(tarea, estado, (conDialogo) => {
      if (conDialogo) setEnfocar({ id, estado });
      seguir();
    });
  };

  const elegirVista = (nueva: Vista) => {
    setVista(nueva);
    guardarPreferencia('vista', nueva);
  };

  return (
    <main className="pagina">
      <header className="pagina__cabecera">
        <div className="pagina__titulo">
          <div className="pagina__titulo-fila">
            <h1>Backlog CDTEC IF</h1>
            <IndicadorRefresco
              actualizadoEn={tareas.isPlaceholderData ? 0 : tareas.dataUpdatedAt}
              actualizando={tareas.isFetching}
              fallo={tareas.isRefetchError}
            />
          </div>
          <p className="pagina__subtitulo">Lo pendiente, quién lo hace y en qué etapa va.</p>
        </div>
        <div className="pagina__acciones">
          <a className="boton" href={urlExportarCsv(filtros)}>
            <Icono nombre="bajar" />
            Exportar CSV
          </a>
          <div className="segmentado segmentado--vista" role="group" aria-label="Vista">
            <button
              type="button"
              aria-pressed={vista === 'tablero'}
              onClick={() => elegirVista('tablero')}
            >
              <Icono nombre="tablero" />
              Tablero
            </button>
            <button
              type="button"
              aria-pressed={vista === 'lista'}
              onClick={() => elegirVista('lista')}
            >
              <Icono nombre="lista" />
              Lista
            </button>
          </div>
        </div>
      </header>

      {todas.isPending ? (
        <ResumenCargando mostrar={mostrarEsqueleto} />
      ) : (
        <ResumenTablero tareas={todas.data?.items ?? null} />
      )}

      <BarraFiltros
        filtros={filtros}
        personas={personas.data ?? []}
        proyectos={proyectos.data ?? []}
        onCambio={(cambios) => setFiltros((actuales) => ({ ...actuales, ...cambios }))}
      />
      <AvisoCerradas filtros={filtros} onAbrir={abrir} />

      {tareas.isPending ? (
        vista === 'tablero' ? (
          <TableroCargando filtroEstado={filtros.estado} mostrar={mostrarEsqueleto} />
        ) : (
          <ListaTareasCargando mostrar={mostrarEsqueleto} />
        )
      ) : tareas.isError && !tareas.data ? (
        <EstadoError
          texto="Revisa tu conexión. Si sigue pasando, avísale a Stephen o Alexander. Tus filtros se mantienen."
          reintentando={tareas.isFetching}
          onReintentar={() => {
            void tareas.refetch();
            if (todas.isError) void todas.refetch();
          }}
        />
      ) : tareas.data && tareas.data.items.length === 0 ? (
        todas.data?.total === 0 ? (
          <EstadoVacio
            titulo="Todavía no hay tareas"
            texto={
              yo.puedeEscribir
                ? 'Crea la primera para empezar a ordenar el trabajo del equipo.'
                : 'Cuando el equipo cree la primera, aparecerá aquí.'
            }
            accion={
              yo.puedeEscribir && (
                <button
                  type="button"
                  className="boton boton--primario boton--con-icono"
                  onClick={nueva}
                >
                  <Icono nombre="mas" />
                  Nueva tarea
                </button>
              )
            }
          />
        ) : (
          <EstadoVacio
            titulo="No hay tareas con estos filtros"
            texto="Prueba quitando algún filtro o buscando otra palabra."
          />
        )
      ) : tareas.data ? (
        vista === 'tablero' ? (
          <Tablero
            tareas={tareas.data.items}
            filtroEstado={filtros.estado}
            puedeEscribir={yo.puedeEscribir}
            moviendo={moviendo}
            onAbrir={abrir}
            onMover={moverTarjeta}
            enfocar={enfocar}
            onEnfocada={olvidarEnfocar}
          />
        ) : (
          <ListaTareas tareas={tareas.data.items} onAbrir={abrir} />
        )
      ) : null}

      {idAbierta !== null && (
        <PanelTarea
          id={idAbierta}
          personas={personas.data ?? []}
          puedeEscribir={yo.puedeEscribir}
          puedeAdministrar={yo.puedeAdministrar}
          yoId={yo.id}
          onCerrar={cerrar}
          onAbrir={abrir}
          padreId={padreNueva}
          onNuevaSubtarea={nuevaSubtarea}
        />
      )}
      {confirmacionCierre}
    </main>
  );
}
